import { EProductCodes, ITenant, IUserRole } from '../domains';
import { QuickAction, QuickActionSchema } from '../lib';
import {
  cdpOpenSegments,
  cdpOpenAudienceSyncs,
  cdpOpenGuests,
  cdpDocumentation,
  cdpApiDocumentation,
  cdpDataBrowser,
  cdpDashboards,
} from './cdp';
import {
  connectOpenDashboardQuickAction,
  connectOpenRecipesQuickAction,
  connectOpenLibraryQuickAction,
  connectDocumentationQuickaction,
  connectTrainingQuickaction,
} from './connect';
import {
  discoverDeveloperResources,
  discoverBrowserContentVisitorCatalog,
  discoverSiteAnalytics,
  discoverNotifications,
  discoverCreateRecipes,
  discoverDocumentationHelpfulLink,
  discoverReactSdkHelpfulLink,
  discoverSdkPlaybookHelpfulLink,
} from './discover';
import {
  headlessCMSManageContentTypeQuickAction,
  headlessCMSManageContentQuickAction,
  headlessCMSManageApiKeysQuickaction,
  headlessCMSManageMediaQuickaction,
  headlessCMSDocumentationQuickAction,
} from './headlessCMS';
import {
  orderCloudApiConsoleQuickAction,
  orderCloudApiReferenceQuickAction,
  orderCloudDeveloperToolsQuickAction,
  orderCloudDocumentationQuickAction,
  orderCloudManageRolesQuickAction,
  orderCloudIndexToolsQuickAction,
} from './order-cloud';
import {
  personalizeDocumentationHelpfulLink,
  personalizeDeveloperDocsHelpfulLink,
  personalizeOpenDecisionModels,
  personalizeOpenExperiences,
  personalizeOpenExperiments,
  personalizeEventViewer,
} from './personalize';
import {
  searchBrowserContentVisitorCatalog,
  searchOpenDeveloperResources,
  searchSetNotifications,
  searchManageSources,
  searchSetSynonyms,
  searchViewContentAnalytics,
  searchDocumentationHelpfulLink,
  searchReactSdkHelpfulLink,
} from './search';
import {
  xmCloudDocumentationQuickAction,
  xmCloudOpenContentEditorQuickAction,
  xmCloudOpenExplorerQuickAction,
  xmCloudOpenPagesQuickAction,
} from './xmcloud';
import {
  sendCreateCampaignQuickAction,
  sendCreateNewEmailListQuickAction,
  sendAutomationsQuickAction,
  sendCreateNewLandingPageQuickAction,
  sendCreateNewSubscriptionFormQuickAction,
  sendDocumentationHelpfulLink,
} from './send';

export const quickActionsPerProductMap = {
  [EProductCodes.XMCloud]: [
    xmCloudOpenPagesQuickAction,
    xmCloudOpenExplorerQuickAction,
    xmCloudOpenContentEditorQuickAction,
    xmCloudDocumentationQuickAction,
  ],
  [EProductCodes.HeadlessCMS]: [
    headlessCMSManageContentTypeQuickAction,
    headlessCMSManageContentQuickAction,
    headlessCMSManageMediaQuickaction,
    headlessCMSManageApiKeysQuickaction,
    headlessCMSDocumentationQuickAction,
  ],
  [EProductCodes.OrderCloud]: [
    orderCloudManageRolesQuickAction,
    orderCloudApiConsoleQuickAction,
    orderCloudDocumentationQuickAction,
    orderCloudApiReferenceQuickAction,
    orderCloudDeveloperToolsQuickAction,
    orderCloudIndexToolsQuickAction,
  ],
  [EProductCodes.Connect]: [
    connectOpenDashboardQuickAction,
    connectOpenRecipesQuickAction,
    connectOpenLibraryQuickAction,
    connectDocumentationQuickaction,
    connectTrainingQuickaction,
  ],
  [EProductCodes.CDP]: [
    cdpOpenGuests,
    cdpOpenSegments,
    cdpOpenAudienceSyncs,
    cdpDashboards,
    cdpDocumentation,
    cdpApiDocumentation,
    cdpDataBrowser,
  ],
  [EProductCodes.Personalize]: [
    personalizeOpenExperiences,
    personalizeOpenExperiments,
    personalizeOpenDecisionModels,
    personalizeEventViewer,
    personalizeDocumentationHelpfulLink,
    personalizeDeveloperDocsHelpfulLink,
  ],
  [EProductCodes.Search]: [
    searchBrowserContentVisitorCatalog,
    searchViewContentAnalytics,
    searchSetNotifications,
    searchManageSources,
    searchSetSynonyms,
    searchOpenDeveloperResources,
    searchDocumentationHelpfulLink,
    searchReactSdkHelpfulLink,
  ],
  [EProductCodes.Discover]: [
    discoverBrowserContentVisitorCatalog,
    discoverSiteAnalytics,
    discoverNotifications,
    discoverCreateRecipes,
    discoverDeveloperResources,
    discoverDocumentationHelpfulLink,
    discoverReactSdkHelpfulLink,
    discoverSdkPlaybookHelpfulLink,
  ],
  [EProductCodes.Send]: [
    sendCreateCampaignQuickAction,
    sendCreateNewEmailListQuickAction,
    sendAutomationsQuickAction,
    sendCreateNewLandingPageQuickAction,
    sendCreateNewSubscriptionFormQuickAction,
    sendDocumentationHelpfulLink,
  ],
};

// Helpful links should always be on the bottom of the array in order for the category to be displayed after the quick actions
// TODO: investigate an improvements
export const getTenantQuickActionsPerProduct = (
  tenant: ITenant,
  userRoles: IUserRole[],
  env,
  activeProducts: string[],
) => {
  const { ProductCode } = tenant.labels;
  return (
    quickActionsPerProductMap[
      ProductCode as keyof typeof quickActionsPerProductMap
    ] ?? []
  )
    .map(
      (json) =>
        new QuickAction({
          jsonData: json as QuickActionSchema,
          tenant,
          userRoles,
          env,
          activeProducts: activeProducts || [],
        }),
    )
    .filter(({ isDisabled, isValid }) => !isDisabled && isValid);
};
